/**
 * Early Leave Request Form — Digi Sahyadri WRMS
 * Matches the physical Early Leave Form exactly
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPaperPlane, FaUserFriends } from 'react-icons/fa';
import WRMSFormHeader from '../shared/WRMSFormHeader';
import SignatureFooter from '../shared/SignatureFooter';
import { submitRequest } from '../../services/wrmsService';
import { playNotificationSound } from '../../../utils/soundService';
import { getEmployeesByDepartment } from '../../../services/firebase/employeeService';
import { getApprovalChain, getDeptGroup, getStageLabelForRole } from '../../utils/ApprovalRouter';

const today = () => new Date().toISOString().split('T')[0];

const EarlyLeaveForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [deptEmployees, setDeptEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Fetch department colleagues for handover dropdown
  useEffect(() => {
    const dept = user?.department;
    if (!dept) return;
    setLoadingEmployees(true);
    getEmployeesByDepartment(dept)
      .then(res => {
        const list = (res.data || []).filter(emp => {
          const empEmail = emp.personal?.email || emp.email || '';
          return empEmail.toLowerCase() !== (user?.email || '').toLowerCase();
        });
        setDeptEmployees(list);
      })
      .catch(() => setDeptEmployees([]))
      .finally(() => setLoadingEmployees(false));
  }, [user?.department, user?.email]);

  const [form, setForm] = useState({
    applicationDate: today(),
    fromDate: today(),
    earlyLeaveDuration: '',
    requestedLeaveTime: '',
    reason: '',
    currentResponsibility: '',
    handoverTo: '',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fromDate || !form.earlyLeaveDuration || !form.requestedLeaveTime || !form.reason) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitRequest('early_leave', form, user);
      playNotificationSound();
      toast.success(`Early leave request submitted! ID: ${result.requestId}`);
      navigate('/wrms');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const deptGroup = getDeptGroup(user?.department, user?.role);
  const chain = getApprovalChain('early_leave', deptGroup);
  
  const signatories = chain.map(step => ({
    title: step.role === 'employee' ? 'Signature of Applicant' : `Signature of ${getStageLabelForRole(step.role)}`,
    name: step.role === 'employee' ? (user?.employeeName || user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '') : null,
    date: step.role === 'employee' ? new Date() : null,
  }));

  return (
    <div>
      <WRMSFormHeader formTitle="Early Leave Request Form" formSubtitle="Digi Sahyadri — Request System" />

      <div className="wrms-form-body">
        <form onSubmit={handleSubmit}>
          {/* Auto-populated Section */}
          <div className="wrms-section-label">Employee Details (Auto-populated)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div className="wrms-form-group">
              <label>Name</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || ''} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Team ID</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeCode || user?.teamId || '—'} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Designation</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.designation || '—'} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Department</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.department || '—'} readOnly />
            </div>
          </div>

          {/* Early Leave Details */}
          <div className="wrms-section-label">Early Leave Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Date of Application</label>
              <input type="date" className="wrms-form-control form-control wrms-field-readonly"
                value={form.applicationDate} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>From Date <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="date" className="wrms-form-control form-control"
                value={form.fromDate}
                onChange={e => handleChange('fromDate', e.target.value)} required />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Early Leave Duration <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="text"
                className="wrms-form-control form-control"
                placeholder='e.g. "2 hours", "1.5 hours"'
                value={form.earlyLeaveDuration}
                onChange={e => handleChange('earlyLeaveDuration', e.target.value)}
                required
              />
            </div>
            <div className="wrms-form-group">
              <label>Requested Early Leave Time <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="time"
                className="wrms-form-control form-control"
                value={form.requestedLeaveTime}
                onChange={e => handleChange('requestedLeaveTime', e.target.value)}
                required
              />
              <small style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px', display: 'block' }}>
                What time do you wish to leave?
              </small>
            </div>
          </div>

          {/* Reason */}
          <div className="wrms-form-group" style={{ marginBottom: '20px' }}>
            <label>Reason for Early Leave <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea className="wrms-form-control form-control" rows={3}
              placeholder="Please describe the reason for early leave..."
              value={form.reason}
              onChange={e => handleChange('reason', e.target.value)}
              required />
          </div>

          {/* Handover */}
          <div className="wrms-section-label">Handover Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Current Responsibility — Priority-wise</label>
              <textarea className="wrms-form-control form-control" rows={3}
                placeholder="List current responsibilities..."
                value={form.currentResponsibility}
                onChange={e => handleChange('currentResponsibility', e.target.value)} />
            </div>
            <div className="wrms-form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaUserFriends size={13} style={{ color: '#4f46e5' }} />
                Current Responsibility Hand Over To
              </label>
              {loadingEmployees ? (
                <div style={{ padding: '10px', fontSize: '0.82rem', color: '#64748b' }}>Loading department employees...</div>
              ) : deptEmployees.length > 0 ? (
                <select
                  className="wrms-form-control form-control"
                  value={form.handoverToId || form.handoverTo}
                  onChange={e => {
                    const val = e.target.value;
                    const selectedEmp = deptEmployees.find(emp => emp.id === val || (emp.personal?.fullName || emp.firstName) === val);
                    if (selectedEmp) {
                      const name = selectedEmp.personal?.fullName ||
                        `${selectedEmp.personal?.firstName || ''} ${selectedEmp.personal?.lastName || ''}`.trim() ||
                        selectedEmp.firstName || 'Unknown';
                      setForm(prev => ({
                        ...prev,
                        handoverTo: name,
                        handoverToId: selectedEmp.id,
                        handoverToEmail: selectedEmp.personal?.email || selectedEmp.email || '',
                        handoverToTeamId: selectedEmp.job?.employeeId || selectedEmp.employeeCode || selectedEmp.teamId || '',
                      }));
                    } else {
                      setForm(prev => ({ ...prev, handoverTo: val, handoverToId: '', handoverToEmail: '', handoverToTeamId: '' }));
                    }
                  }}
                  style={{ height: '42px' }}
                >
                  <option value="">— Select employee —</option>
                  {deptEmployees.map(emp => {
                    const name = emp.personal?.fullName ||
                      `${emp.personal?.firstName || ''} ${emp.personal?.lastName || ''}`.trim() ||
                      emp.firstName || 'Unknown';
                    const designation = emp.job?.designation || emp.designation || '';
                    return (
                      <option key={emp.id} value={emp.id}>
                        {name}{designation ? ` — ${designation}` : ''}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <input type="text" className="wrms-form-control form-control"
                  placeholder="Enter employee name (no dept colleagues found)"
                  value={form.handoverTo}
                  onChange={e => handleChange('handoverTo', e.target.value)} />
              )}
              {deptEmployees.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                  Showing {deptEmployees.length} active colleague{deptEmployees.length !== 1 ? 's' : ''} from your department · Selected colleague will receive handover request for approval first
                </div>
              )}
            </div>
          </div>

          <div className="wrms-form-group" style={{ marginBottom: '28px' }}>
            <label>Application Approved On (System auto-fills on final approval)</label>
            <input className="wrms-form-control wrms-field-readonly form-control"
              value="Pending Final Approval" readOnly />
          </div>

          <SignatureFooter signatories={signatories} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <button type="button" className="wrms-btn-outline"
              onClick={() => navigate('/wrms')} style={{ padding: '10px 24px' }}>
              Cancel
            </button>
            <button type="submit" className="wrms-btn-primary" disabled={submitting}>
              <FaPaperPlane size={13} />
              {submitting ? 'Submitting...' : 'Submit Early Leave Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EarlyLeaveForm;
